import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { CapacityByPersonResponse, FilterOptions } from '../../models/capacity-by-person.models';
import { environment } from 'src/environments/environment';

@Injectable({
  providedIn: 'root'
})
export class CapacityByPersonService {
  private apiUrl = `${environment.main_url}/agilelegal/reports/tasks/capacity-by-person`;
  private tkUsersUrl = `${environment.main_url}${environment.lpcUsersList}`;
  private practiceAreaUrl = `${environment.main_url}${environment.getPracticeArea}`;
  private projectTypesUrl = `${environment.main_url}${environment.projectTypesList}`;
  
  constructor(private http: HttpClient) { }

  /**
   * Fetch capacity by person data from API
   * @param filters Filter options for the API request
   * @returns Observable of CapacityByPersonResponse
   */
  getCapacityByPersonData(filters: FilterOptions): Observable<CapacityByPersonResponse> {
    let params = new HttpParams();
    
    // Add all filter parameters to the query, only if they have actual values
    if (filters.startDate && filters.startDate !== '') {
      params = params.set('startDate', filters.startDate);
    }
    
    if (filters.endDate && filters.endDate !== '') {
      params = params.set('endDate', filters.endDate);
    }
    
    if (filters.tkId !== null) {
      params = params.set('tkMainEmail', filters.tkId.toString());
    }
    
    if (filters.practiceAreaId !== null) {
      params = params.set('practiceArea', filters.practiceAreaId.toString());
    }
    
    
    return this.http.get<CapacityByPersonResponse>(this.apiUrl, { params })
      .pipe(
        catchError(error => {
          console.error('Error fetching capacity by person data:', error);
          return of({ 
            data: [], 
            message: 'Failed to fetch capacity by person data',
            customValues: null
          });
        })
      );
  }

  /**
   * Fetches practice areas from API
   */
  getPracticeAreas(): Observable<any> {
    return this.http.get<any>(this.practiceAreaUrl)
      .pipe(
        map(response => {
          if (response && response.data && Array.isArray(response.data)) {
            return {
              data: response.data.map((area: any) => ({
                id: area.description,  // Using description as both id and name as requested
                name: area.description
              })),
              message: response.message,
              customValues: response.customValues
            };
          }
          return response;
        }),
        catchError(error => {
          console.error('Error fetching practice areas:', error);
          return of({ data: [], success: false, message: 'Failed to fetch practice areas' });
        })
      );
  }

  /**
   * Fetches TK users for the filter dropdown
   */
  getTKUsers(): Observable<any> {
    // Use the same API endpoint as LPC users but with TK role
    const payload = {
      "roles": ["TK"],  // Using TK role instead of LPC
      "userTypes": []
    };
    
    return this.http.post<any>(this.tkUsersUrl, payload)
      .pipe(
        map(response => {
          if (response && response.data && Array.isArray(response.data)) {
            return {
              data: response.data.map((user: any) => {
                // Combine firstName, middleName, and lastName
                const nameParts = [
                  user.firstName || '',
                  user.middleName || '',
                  user.lastName || ''
                ].filter(part => part.trim() !== '');
                
                const fullName = nameParts.join(' ').trim();
                
                return {
                  id: user.mainEmail,
                  name: fullName || user.mainEmail // Fallback to email if no name
                };
              }),
              message: response.message,
              customValues: response.customValues
            };
          }
          return response;
        }),
        catchError(error => {
          console.error('Error fetching TK users:', error);
          return of({ data: [], success: false, message: 'Failed to fetch TK users' });
        })
      );
  }
}
